import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  citDocumentStatusClass,
  formatCitQty,
  type CitStListLine,
  type CitStMonthGroup,
} from '../../lib/citList';

type Props = {
  groups: CitStMonthGroup[];
  detailPath: (submissionId: string) => string;
};

type CitStEntry = {
  key: string;
  submissionId: string;
  stockTransferNo: string;
  salesConfirmationNo: string;
  customerCode: string;
  customerName: string;
  status: string;
  items: CitStListLine[];
};

function groupLinesByStockTransfer(lines: CitStListLine[]): CitStEntry[] {
  const entries: CitStEntry[] = [];
  const indexByKey = new Map<string, number>();

  for (const line of lines) {
    const entryKey = `${line.submissionId}:${line.stockTransferNo}`;
    const existingIndex = indexByKey.get(entryKey);
    if (existingIndex != null) {
      entries[existingIndex].items.push(line);
      continue;
    }
    indexByKey.set(entryKey, entries.length);
    entries.push({
      key: entryKey,
      submissionId: line.submissionId,
      stockTransferNo: line.stockTransferNo,
      salesConfirmationNo: line.salesConfirmationNo,
      customerCode: line.customerCode,
      customerName: line.customerName,
      status: line.status,
      items: [line],
    });
  }

  return entries;
}

function CitStListRow({ entry, detailPath }: { entry: CitStEntry; detailPath: string }) {
  return (
    <Link to={detailPath} className="cit-st-list-row">
      <span className="cit-st-list-row__st mono">{entry.stockTransferNo || '—'}</span>

      <span className="cit-st-list-row__metrics">
        <span className="cit-st-list-row__metrics-head" aria-hidden>
          <span>Item</span>
          <span>UOM</span>
          <span>Offset qty</span>
        </span>
        {entry.items.length === 0 ? (
          <span className="cit-st-list-row__item-empty">No items</span>
        ) : (
          entry.items.map((item) => (
            <span key={item.key} className="cit-st-list-row__metric-line">
              <span className="cit-st-list-row__item mono">{item.itemNo || '—'}</span>
              <span className="cit-st-list-row__uom">{item.uom || '—'}</span>
              <span className="cit-st-list-row__qty">{formatCitQty(item.offsetQty)}</span>
            </span>
          ))
        )}
      </span>

      <span className="cit-st-list-row__sc mono">{entry.salesConfirmationNo || '—'}</span>

      <span className="cit-st-list-row__customer">
        {entry.customerCode ? (
          <span className="cit-st-list-row__customer-code mono">{entry.customerCode}</span>
        ) : null}
        <span className="cit-st-list-row__customer-name">
          {entry.customerName || (entry.customerCode ? '' : '—')}
        </span>
      </span>

      <span className={citDocumentStatusClass(entry.status)}>{entry.status || '—'}</span>

      <span className="cit-st-list-row__chevron" aria-hidden>
        ›
      </span>
    </Link>
  );
}

export function CitStList({ groups, detailPath }: Props) {
  const monthKeySig = useMemo(() => groups.map((group) => group.monthKey).join('|'), [groups]);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const keys = monthKeySig ? monthKeySig.split('|') : [];
    setCollapsed((prev) => {
      const next: Record<string, boolean> = {};
      keys.forEach((key, index) => {
        next[key] = prev[key] ?? index > 0;
      });
      return next;
    });
  }, [monthKeySig]);

  if (groups.length === 0) return null;

  return (
    <div className="cit-st-list">
      {groups.map((group) => {
        const open = !collapsed[group.monthKey];
        const entries = groupLinesByStockTransfer(group.lines);
        const itemCount = group.lines.length;
        return (
          <section key={group.monthKey} className="cit-st-month card">
            <button
              type="button"
              className="cit-st-month__toggle"
              aria-expanded={open}
              onClick={() =>
                setCollapsed((prev) => ({
                  ...prev,
                  [group.monthKey]: !prev[group.monthKey],
                }))
              }
            >
              <span className="cit-st-month__chevron" aria-hidden>
                {open ? '▾' : '▸'}
              </span>
              <span className="cit-st-month__title">{group.monthLabel}</span>
              <span className="cit-st-month__meta">
                {entries.length} stock transfer{entries.length === 1 ? '' : 's'} · {itemCount} item
                {itemCount === 1 ? '' : 's'}
              </span>
            </button>

            {open ? (
              <div className="cit-st-month__body">
                <div className="cit-st-list__head" role="row">
                  <span>Stock transfer no.</span>
                  <span>Item / UOM / Offset qty</span>
                  <span>Sales confirmation</span>
                  <span>Customer</span>
                  <span>Status</span>
                  <span aria-hidden />
                </div>
                {entries.map((entry) => (
                  <CitStListRow
                    key={entry.key}
                    entry={entry}
                    detailPath={detailPath(entry.submissionId)}
                  />
                ))}
              </div>
            ) : null}
          </section>
        );
      })}
    </div>
  );
}
